import React, { FC, createContext, useState, useContext, useEffect } from "react";

import { TeamContext } from "../context";
import { setNewAnglesForEachUser, setUserInitialAngles, solutionCardsInitialVisibility } from "./helpers";
import { UserContext } from "./UserContext";

interface ITableNotification {
    firstLineBefore: string;
    firstLineOwner: string;
    ownerColor: string,
    firstLineAftar: string;
    secondLine: string;
    thirdLine: string
}

interface ITableContext {
 
    areAnimatedHandsShown: boolean;
    isEmotionalCardVisible: boolean;
    isSocialCardVisible: boolean; 
    isMentalCardVisible: boolean; 
    isPhysicalCardVisible: boolean; 
    userAngles: any;
    oldUserAngles: any;
    solutionCardExchanged: number;
    isLogoOnTheTableBlured: boolean;
    tableNotification: ITableNotification | undefined;
    theIndexOfTheUserPositionedAt270Degrees: number;
    areAvatarsCurentlyResting: boolean; 
    strengthCardsAnimationDuringAvatarRotation: boolean;
    isUpdatingStatesDisabled: boolean; 
    isJokerButtonHovered: boolean;
    isDilemmaChosen: boolean; 
    toggleMySolutionFullScreen: boolean;
    didTheCurrentUserProposedSolution: boolean;
    setSolutionCardExchanged(solutionCardExchanged: number): void;
    setAreAnimatedHandsShown(areAnimatedHandsShown: boolean): void;
    setIsEmotionalCardVisible(isEmotionalCardVisible: boolean): void;
    setIsSocialCardVisible(isSocialCardVisible: boolean): void;
    setIsMentalCardVisible(isMentalCardVisible: boolean): void;
    setIsPhysicalCardVisible(isEmotionalCardVisible: boolean): void;
    updateUserAngles(angleOfTheSelectedAvatar: number): any;
    setIsLogoOnTheTableBlured(isLogoOnTheTableBlured: boolean): void;
    setTheIndexOfTheUserPositionedAt270Degrees(theIndexOfTheUserPositionedAt270Degrees: number): void;
    setTableNotification(tableNotification: ITableNotification | undefined): void;
    setOldUserAngles(oldUserAngles: any): void;
    setAreAvatarsCurentlyResting(areAvatarsCurentlyResting: boolean): void;
    setStrengthCardsAnimationDuringAvatarRotation(setStrengthCardsAnimationDuringAvatarRotation: boolean): void;
    setIsUpdatingStatesDisabled(setIsUpdatingStatesDisabled: boolean): void;
    setIsJokerButtonHovered(isJokerButtonHovered: boolean): void;
    setIsDilemmaChosen(isDilemmaChosen: boolean): void;
    setToggleMySolutionFullScreen(toggleMySolutionFullScreen: boolean): void;
    setDidTheCurrentUserProposedSolution(didTheCurrentUserProposedSolution: boolean): void;
};

const TableContext = createContext({} as ITableContext);

interface TableContextProps {
    children: React.ReactNode;
};

const TableContextProvider: FC<TableContextProps> = (props) => {
    const { game, numberOfPlayers } = useContext(TeamContext);
    const { userData } = useContext(UserContext);
    const [areAnimatedHandsShown, setAreAnimatedHandsShown] = useState<boolean>(true);

    const [isEmotionalCardVisible, setIsEmotionalCardVisible] = useState<boolean>(solutionCardsInitialVisibility(game, userData));
    const [isSocialCardVisible,    setIsSocialCardVisible] =    useState<boolean>(solutionCardsInitialVisibility(game, userData));
    const [isMentalCardVisible,    setIsMentalCardVisible] =    useState<boolean>(solutionCardsInitialVisibility(game, userData));
    const [isPhysicalCardVisible,  setIsPhysicalCardVisible] =  useState<boolean>(solutionCardsInitialVisibility(game, userData));

    const [isUpdatingStatesDisabled, setIsUpdatingStatesDisabled] =  useState<boolean>(false);
    
    const [userAngles, setUserAngles] = useState<any>(setUserInitialAngles(game, userData));
    const [oldUserAngles, setOldUserAngles] = useState<any>(setUserInitialAngles(game, userData));
    const [isLogoOnTheTableBlured, setIsLogoOnTheTableBlured] = useState<boolean>(false);
    const [theIndexOfTheUserPositionedAt270Degrees, setTheIndexOfTheUserPositionedAt270Degrees] = useState<number>(numberOfPlayers - 1 | 0);
    const [tableNotification, setTableNotification] = useState<ITableNotification | undefined>(undefined);
    const [areAvatarsCurentlyResting, setAreAvatarsCurentlyResting] = useState<boolean>(true);
    const [strengthCardsAnimationDuringAvatarRotation, setStrengthCardsAnimationDuringAvatarRotation] = useState<boolean>(false);
    const [isJokerButtonHovered, setIsJokerButtonHovered] = useState<boolean>(false);
    const [isDilemmaChosen, setIsDilemmaChosen] = useState<boolean>(false);
    const [toggleMySolutionFullScreen, setToggleMySolutionFullScreen] = useState<boolean>(true);
    const [didTheCurrentUserProposedSolution, setDidTheCurrentUserProposedSolution] = useState<boolean>(false);
    const [solutionCardExchanged, setSolutionCardExchanged] = useState<number>(0);

    useEffect(() => {
        setUserAngles(setUserInitialAngles(game, userData));
    }, [game, userData]);

    useEffect(() => {
        if(isUpdatingStatesDisabled) return;
        
        setIsEmotionalCardVisible(solutionCardsInitialVisibility(game, userData));
        setIsSocialCardVisible(solutionCardsInitialVisibility(game, userData));
        setIsMentalCardVisible(solutionCardsInitialVisibility(game, userData));
        setIsPhysicalCardVisible(solutionCardsInitialVisibility(game, userData));
    }, [game, isUpdatingStatesDisabled, userData]);

    const updateUserAngles = (selectedUserIndex: number): any => {
        setOldUserAngles(userAngles);
        let newUserAnglesValues = setNewAnglesForEachUser(selectedUserIndex, userAngles);
        setUserAngles(newUserAnglesValues);
    }

    const providerValue = {
        areAnimatedHandsShown,
        isEmotionalCardVisible,
        isSocialCardVisible, 
        isMentalCardVisible, 
        isPhysicalCardVisible, 
        userAngles,
        isLogoOnTheTableBlured,
        theIndexOfTheUserPositionedAt270Degrees,
        tableNotification, 
        oldUserAngles,
        areAvatarsCurentlyResting, 
        strengthCardsAnimationDuringAvatarRotation,
        isUpdatingStatesDisabled, 
        isJokerButtonHovered,
        solutionCardExchanged,
        isDilemmaChosen, 
        toggleMySolutionFullScreen, 
        didTheCurrentUserProposedSolution, 
        setAreAnimatedHandsShown,
        setIsEmotionalCardVisible,
        setIsSocialCardVisible,
        setIsMentalCardVisible,
        setIsPhysicalCardVisible,
        updateUserAngles,
        setIsLogoOnTheTableBlured,
        setTheIndexOfTheUserPositionedAt270Degrees,
        setSolutionCardExchanged,
        setTableNotification,
        setOldUserAngles,
        setAreAvatarsCurentlyResting,
        setStrengthCardsAnimationDuringAvatarRotation,
        setIsUpdatingStatesDisabled,
        setIsJokerButtonHovered,
        setIsDilemmaChosen,
        setToggleMySolutionFullScreen,
        setDidTheCurrentUserProposedSolution
    };

    return (
        <TableContext.Provider value={providerValue}>
            {props.children}
        </TableContext.Provider>
    );
};

export { TableContext, TableContextProvider };